// May 28, 2026 — Dashboard Operations split-system trackers.
//
// Per user spec, the dashboard shows TWO independent rows of provider
// pills:
//
//   • RED row (platform vouchers) — one pill per provider where the user
//     has NO BYOK key. Number is the cross-silo sum of that provider's
//     voucher bucket. Decrements on every PLATFORM call at 3× markup.
//     Stripe top-ups fund it. Redistribute popup can shuffle between
//     silos but cannot change the total (it's locked to what's paid in).
//
//   • GREEN row (BYOK self-balance) — one pill per provider where the
//     user HAS a BYOK key. Number is what the user typed in as their
//     real balance at Google AI Studio / Anthropic / OpenAI. Decrements
//     on every BYOK call at RAW cost (no markup — they pay direct).
//     Redistribute popup IS editable here (user can re-sync to their
//     actual balance any time).
//
// A provider appears in EXACTLY ONE row at a time. Empty rows hide.
//
// No event-listener auto-refresh (user reverted that request — see
// May 28 evening — pills load on mount only).
import { useEffect, useState } from "react";
import { Wallet, Info, Minus, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import VoucherHeldStrip from "@/components/VoucherHeldStrip";
import VoucherRestrictionNotice from "@/components/VoucherRestrictionNotice";
import VoucherActivityFeed from "@/components/VoucherActivityFeed";
import { ProviderTrackerTile, RedistributePopup } from "@/components/ProviderTrackerCore";

const PROV_IDS = ["gemini", "anthropic", "openai"];

export default function ProviderTrackersCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupProvider, setTopupProvider] = useState(null);
  const [redistProvider, setRedistProvider] = useState(null);
  const [redistByokMode, setRedistByokMode] = useState(false);
  // Optional reveal of the hidden red (platform voucher) tiles even
  // when the user has BYOK on a provider. Use case: spot stale funds
  // that should have been swept to their earnings bucket when they
  // added the BYOK key (auto-sweep is best-effort).
  const [revealHiddenRed, setRevealHiddenRed] = useState(false);
  const [flushBusy, setFlushBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/voucher-summary");
      setSummary(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load voucher trackers.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !summary) {
    return (
      <div className="bg-white border hairline rounded-lg p-3" data-testid="provider-trackers-loading">
        <div className="text-sm text-[#6B6A68]">Loading provider trackers…</div>
      </div>
    );
  }

  const byok = summary.byok_status || {};
  const platformBuckets = summary.provider_totals_cents || {};
  const byokBalances = summary.byok_self_balance_cents || {};
  const spent30 = summary.provider_spent_30d_cents || {};
  const isFounder = !!summary.is_founder;

  const redProviders = PROV_IDS.filter((p) => !byok[p]);
  const greenProviders = PROV_IDS.filter((p) => !!byok[p]);
  const redProvidersDisplay = revealHiddenRed ? PROV_IDS : redProviders;
  const hasHiddenRedFunds = greenProviders.some((p) => (platformBuckets[p] || 0) > 0);
  const totalPlatformCents = PROV_IDS.reduce((a, p) => a + (platformBuckets[p] || 0), 0);

  const openRedistribute = (prov, byokModeFlag) => {
    setRedistByokMode(!!byokModeFlag);
    setRedistProvider(prov);
  };

  const onFlush = async () => {
    if (totalPlatformCents <= 0) {
      toast.info("No platform voucher funds to flush.");
      return;
    }
    const dollars = (totalPlatformCents / 100).toFixed(2);
    const dest = isFounder ? "the Platform LLM bucket (super admin financials)" : "your Earnings (Stripe Express)";
    if (!window.confirm(`Flush $${dollars} across all your platform vouchers into ${dest}? This zeroes every red bucket.`)) return;
    setFlushBusy(true);
    try {
      const r = await api.post("/me/voucher/flush", {});
      const swept = r.data?.swept_cents || 0;
      toast.success(`Flushed $${(swept / 100).toFixed(2)} → ${r.data?.destination || dest}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flush failed");
    } finally {
      setFlushBusy(false);
    }
  };

  return (
    <>
      <VoucherRestrictionNotice restrictions={summary.restrictions} context="dashboard" />
      {/* Jun 2026 (Pass 3) — available / held / total. Renders only when credit
          is actually frozen by an open dispute, so a balance never appears to
          shrink without explanation. */}
      <VoucherHeldStrip
        availableCents={totalPlatformCents}
        heldCents={summary.credit_held_cents}
        disputeCount={(summary.silos || []).filter((s) => (s.frozen_voucher_cents || 0) > 0).length}
        context="dashboard"
      />
      {redProvidersDisplay.length > 0 && (
        <div className="bg-white border hairline rounded-lg p-3 mb-3" data-testid="provider-trackers-red-row">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} weight="duotone" className="text-[#C16E5A] shrink-0" />
            <div className="text-xs font-medium text-[#1C1B1A]">
              Platform vouchers · pay-as-you-go ({redProvidersDisplay.length})
              {revealHiddenRed && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A89360]">· revealed</span>
              )}
            </div>
            <span
              className="inline-flex items-center text-[#6B6A68] hover:text-[#1C1B1A] cursor-help"
              data-testid="provider-trackers-red-info"
              title="Top up via Stripe and pay 3× the raw LLM cost — the platform covers the underlying provider bill. Add a BYOK key for that provider to switch to 1× direct pricing (current voucher balance gets refunded to your Stripe Express)."
            >
              <Info size={12} weight="duotone" />
            </span>
            <button
              type="button"
              onClick={onFlush}
              disabled={flushBusy || totalPlatformCents <= 0}
              data-testid="provider-trackers-flush-button"
              title={isFounder ? "Move all platform voucher funds into the Platform LLM bucket" : "Move all platform voucher funds into your Stripe earnings"}
              className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#A89360] text-[#7A5C20] hover:bg-[#FAF2DA] disabled:opacity-50"
            >
              <ArrowsLeftRight size={11} weight="bold" />
              {flushBusy ? "Flushing…" : isFounder ? "Flush to LLM bucket" : "Flush to earnings"}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {redProvidersDisplay.map((p) => (
              <ProviderTrackerTile
                key={p}
                provider={p}
                byok={false}
                bucketCents={platformBuckets[p] || 0}
                spent30Cents={spent30[p] || 0}
                peek={{ context: "dashboard", focusSiloId: null }}
                onRedistributeClick={(prov) => openRedistribute(prov, false)}
                onTopupClick={(prov) => setTopupProvider(prov)}
              />
            ))}
          </div>
        </div>
      )}

      {greenProviders.length > 0 && (
        <div className="bg-white border hairline rounded-lg p-3" data-testid="provider-trackers-green-row">
          {/* Tiny dash above the green row — reveals/hides the red row's
              tiles even for providers where the user has BYOK, so they can
              spot stale platform voucher funds that need sweeping. */}
          <div className="flex items-center justify-end mb-1 -mt-1">
            <button
              type="button"
              onClick={() => setRevealHiddenRed((v) => !v)}
              data-testid="provider-trackers-reveal-red-toggle"
              title={revealHiddenRed ? "Hide platform voucher tiles" : "Show hidden platform voucher tiles (check for stale funds)"}
              className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border transition-colors ${revealHiddenRed ? "border-[#C16E5A] text-[#C16E5A] bg-[#FDF1ED]" : "border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360] hover:text-[#1C1B1A]"}`}
            >
              <Minus size={9} weight="bold" />
              {revealHiddenRed ? "Hide platform tiles" : "Show platform tiles"}
              {!revealHiddenRed && hasHiddenRedFunds && (
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#C16E5A]" title="Stale platform funds detected" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
            <div className="text-xs font-medium text-[#1C1B1A]">
              Your provider balances · BYOK direct ({greenProviders.length})
            </div>
            <span
              className="inline-flex items-center text-[#6B6A68] hover:text-[#1C1B1A] cursor-help"
              data-testid="provider-trackers-green-info"
              title="Your own LLM keys. The number is what you told us you have at Google / Anthropic / OpenAI, split across your silos. Every BYOK call drops the matching silo's slice at raw cost. Click Redistribute to re-sync to your real balance any time."
            >
              <Info size={12} weight="duotone" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {greenProviders.map((p) => (
              <ProviderTrackerTile
                key={p}
                provider={p}
                byok={true}
                bucketCents={byokBalances[p] || 0}
                spent30Cents={spent30[p] || 0}
                peek={{ context: "dashboard", focusSiloId: null }}
                onRedistributeClick={(prov) => openRedistribute(prov, true)}
                onTopupClick={(prov) => setTopupProvider(prov)}
              />
            ))}
          </div>
        </div>
      )}

      <VoucherActivityFeed limit={12} />

      <VoucherTopupModal
        open={!!topupProvider}
        onClose={() => setTopupProvider(null)}
        onSuccess={load}
        defaultProvider={topupProvider}
      />
      <RedistributePopup
        open={!!redistProvider}
        onClose={() => setRedistProvider(null)}
        provider={redistProvider}
        byokMode={redistByokMode}
        onSaved={load}
      />
    </>
  );
}
